<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetPropertyGeneralData',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    property: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const units = ref(['ha', 'm2']);
  const types = ref(null);
  const ux = ref([]);
  const b_1040167445267876_parameters = computed(() => ({
    role: 'own',
  }));

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    units: units.value,
    types: types.value,
    ux: ux.value,
    b_1040167445267876_parameters: b_1040167445267876_parameters.value,
  }));

  const $$load_types = async () => {
    return $fn.value
      .fetch('list_property_types')
      .then((p) => _.orderBy(p, ['name']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'units' },
      { name: 'types' },
      { name: 'ux' },
      { name: 'b_1040167445267876_parameters', nodeId: '1040167445267876' },
    ],
    loaders: { types: $$load_types },
    ux: {},
    dataRefs: { labelAlign, units, types, ux, b_1040167445267876_parameters },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isPropertyUnit = computed(() => {
    return props.property.object === 'properyt_unit';
  });

  watch(
    () => props.property,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('property');
    }
  );

  const act_7315092382398562_reloadComponentData_fsseptiyh =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_ch3qtvqgt =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_fsseptiyh,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_ch3qtvqgt],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_ch3qtvqgt,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_ch3qtvqgt = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_ch3qtvqgt.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <legend class="text-muted">
      {{ pgettext('Property', 'General') }}
    </legend>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="owner"
      :label="pgettext('Property', 'Own company')"
      :labelAlign="labelAlign"
      required
    >
      <UcCompanySelector
        :item="property"
        path="owner"
        :parameters="b_1040167445267876_parameters"
        required
        :readonly="property.isReadonly"
        autofocus
      >
      </UcCompanySelector>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="name"
      :label="pgettext('Property', 'Name')"
      :labelAlign="labelAlign"
      required
    >
      <ItpFormInput
        name="name"
        v-model="property.name"
        :value="property.name"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.name =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        required
        :readonly="property.isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="4"
      name="code"
      :label="pgettext('Property', 'Code')"
      :labelAlign="labelAlign"
      :required="isPropertyUnit"
      v-if="isPropertyUnit"
    >
      <ItpFormInput
        name="code"
        v-model="property.code"
        :value="property.code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :required="isPropertyUnit"
        :readonly="property.isReadonly"
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Property', 'Description')"
      :labelAlign="labelAlign"
    >
      <ItpFormTextarea
        name="description"
        v-model="property.description"
        :value="property.description"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.description =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormTextarea>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="description"
      :label="pgettext('Property', 'Type')"
      :labelAlign="labelAlign"
    >
      <ItpFormSelect2
        name="property_type"
        v-model="property.property_type"
        :value="property.property_type"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.property_type =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="types"
        valueField="name"
        textField="name"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :content-cols-md="8"
      name="location_code"
      :label="pgettext('Property', 'Location')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="location_code"
        v-model="property.location_code"
        :value="property.location_code"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.location_code =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="lot_number"
      :label="pgettext('Property', 'Lot number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="lot_number"
        v-model="property.lot_number"
        :value="property.lot_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.lot_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      name="house_number"
      :label="pgettext('Property', 'Building number')"
      :labelAlign="labelAlign"
    >
      <ItpFormInput
        name="house_number"
        v-model="property.house_number"
        :value="property.house_number"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.house_number =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpFormInput>
    </ItpFormGroup>
    <ItpFormGroup
      :label-cols-md="4"
      :label="pgettext('Property', 'Area')"
      :labelAlign="labelAlign"
      contentClass="input-group"
    >
      <ItpFormInput
        type="number"
        name="area"
        v-model="property.area"
        :value="property.area"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.area =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        class="col-8"
      >
      </ItpFormInput>
      <ItpFormSelect2
        name="unit_of_area"
        v-model="property.unit_of_area"
        :value="property.unit_of_area"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (property.unit_of_area =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
        :options="units"
        class="col-4"
      >
      </ItpFormSelect2>
    </ItpFormGroup>
  </ItpUsercontrol>
</template>
